import { cva, type VariantProps } from 'class-variance-authority';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import type * as React from 'react';
import { cn } from '@/lib/utils';

const alertVariants = cva('flex items-start gap-2.5 rounded-[var(--radius-control)] border px-3.5 py-3 text-sm', {
  variants: {
    tone: {
      danger: 'border-danger/25 bg-danger-soft text-danger',
      warn: 'border-warn/25 bg-warn-soft text-warn',
      ok: 'border-ok/25 bg-ok-soft text-ok',
      info: 'border-info/25 bg-info-soft text-info',
    },
  },
  defaultVariants: { tone: 'danger' },
});

const ICON = { danger: XCircle, warn: AlertTriangle, ok: CheckCircle2, info: Info } as const;

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {}

/**
 * Inline feedback attached to a form or a section. The icon is chosen by tone,
 * so meaning is carried by shape as well as colour.
 */
export function Alert({ className, tone = 'danger', children, ...props }: AlertProps) {
  const Icon = ICON[tone ?? 'danger'];
  return (
    <div role="alert" className={cn(alertVariants({ tone }), className)} {...props}>
      <Icon className="mt-0.5 size-[18px] shrink-0" aria-hidden="true" />
      <span className="min-w-0">{children}</span>
    </div>
  );
}
